/**
 * Reloj lógico híbrido (HLC). Formato textual de ancho fijo para que la
 * comparación lexicográfica coincida con el orden causal:
 *   <ms 15 dígitos>:<contador base36 4 caracteres>:<nodo>
 */
export interface HLCParts {
  wall: number;
  counter: number;
  node: string;
}

export const HLC_ZERO = '000000000000000:0000:0';
const HLC_RE = /^(\d{15}):([0-9a-z]{4}):([A-Za-z0-9_-]{1,40})$/;

export function formatHLC(p: HLCParts): string {
  return `${String(p.wall).padStart(15, '0')}:${p.counter.toString(36).padStart(4, '0')}:${p.node}`;
}

export function parseHLC(s: string): HLCParts {
  const m = HLC_RE.exec(s);
  if (!m) throw new Error(`HLC no válido: ${s}`);
  return { wall: Number(m[1]), counter: parseInt(m[2]!, 36), node: m[3]! };
}

export function isValidHLC(s: unknown): s is string {
  return typeof s === 'string' && HLC_RE.test(s);
}

export function compareHLC(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

const MAX_COUNTER = 36 ** 4 - 1;

export class HybridClock {
  private wall = 0;
  private counter = 0;

  constructor(
    readonly node: string,
    private readonly now: () => number = () => Date.now(),
    last?: string,
  ) {
    if (!/^[A-Za-z0-9_-]{1,40}$/.test(node)) throw new Error('Identificador de nodo no válido');
    if (last && isValidHLC(last)) {
      const p = parseHLC(last);
      this.wall = p.wall;
      this.counter = p.counter;
    }
  }

  /** Genera una nueva marca para un evento local. */
  tick(): string {
    const phys = this.now();
    if (phys > this.wall) {
      this.wall = phys;
      this.counter = 0;
    } else {
      this.counter++;
      if (this.counter > MAX_COUNTER) {
        this.wall++;
        this.counter = 0;
      }
    }
    return formatHLC({ wall: this.wall, counter: this.counter, node: this.node });
  }

  /** Incorpora una marca remota para garantizar que las siguientes sean posteriores. */
  receive(remote: string): void {
    const r = parseHLC(remote);
    const phys = this.now();
    const wall = Math.max(this.wall, r.wall, phys);
    if (wall === this.wall && wall === r.wall) this.counter = Math.max(this.counter, r.counter) + 1;
    else if (wall === this.wall) this.counter++;
    else if (wall === r.wall) this.counter = r.counter + 1;
    else this.counter = 0;
    this.wall = wall;
  }

  current(): string {
    return formatHLC({ wall: this.wall, counter: this.counter, node: this.node });
  }
}
